/**
 * Configuration options for creating a debounced refetcher
 */
export interface DebouncedRefetcherConfig {
  /** Initial debounce delay in milliseconds before refetching */
  debounceMs?: number;
  /** Minimum interval in milliseconds between refetches */
  minIntervalMs?: number;
  /** Optional callback when refetching starts */
  onRefetchingStart?: (context?: any) => void;
  /** Optional callback when refetching ends */
  onRefetchingEnd?: (context?: any) => void;
}

/**
 * Creates a reusable debounced refetcher that handles debouncing, rate limiting,
 * and queuing for any refetch/invalidate function.
 *
 * @param refetchFn - The function to call when refetching (receives the context passed to trigger)
 * @param config - Configuration options for debouncing and rate limiting
 * @returns A function that can be called to trigger a debounced refetch with an optional context
 */
export function createDebouncedRefetcher<TContext = void>(
  refetchFn: (context?: TContext) => Promise<void> | void,
  config: DebouncedRefetcherConfig = {},
): (context?: TContext) => Promise<void> {
  const {
    debounceMs = 300,
    minIntervalMs = 500,
    onRefetchingStart,
    onRefetchingEnd,
  } = config;

  // State for this refetcher instance
  let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastRefetchTime = 0;
  let isRefetching = false;
  let queuedRefetch: { context?: TContext } | null = null;

  /**
   * Performs the actual refetch with rate limiting
   */
  const performRefetch = async (context?: TContext): Promise<void> => {
    const now = Date.now();
    const timeSinceLastRefetch = now - lastRefetchTime;

    // Enforce minimum interval between refetches
    if (timeSinceLastRefetch < minIntervalMs) {
      await new Promise((resolve) =>
        setTimeout(resolve, minIntervalMs - timeSinceLastRefetch),
      );
    }

    isRefetching = true;
    lastRefetchTime = Date.now();
    onRefetchingStart?.(context);

    // Perform the refetch
    await refetchFn(context);

    // Reset refetching state after a short delay
    setTimeout(() => {
      onRefetchingEnd?.(context);
      isRefetching = false;

      // If a new refetch was queued while we were refetching, process it
      if (queuedRefetch) {
        const queuedContext = queuedRefetch.context;
        queuedRefetch = null;
        // Trigger a new debounced refetch
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }
        debounceTimeout = setTimeout(async () => {
          debounceTimeout = null;
          await performRefetch(queuedContext);
        }, debounceMs);
      }
    }, 100);
  };

  /**
   * Triggers a debounced refetch
   */
  return async (context?: TContext): Promise<void> => {
    // If a refetch is in progress, queue this request
    if (isRefetching) {
      queuedRefetch = { context };
      return;
    }

    // Clear any existing debounce timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      debounceTimeout = null;
    }

    // Debounce: wait before refetching
    debounceTimeout = setTimeout(async () => {
      debounceTimeout = null;
      await performRefetch(context);
    }, debounceMs);
  };
}
